import { EmptyTableState, Loading, useApiClient } from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';
import { Gift, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { GuestListHeader } from './GuestListHeader';
import { GuestOffersTable, type UserOffer } from './GuestOffersTable';

interface UserOffersResponse {
  user: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    imageUrl: string | null;
  };
  offers: UserOffer[];
}

export function HotelUserOffersTab() {
  const { hotelId, userId } = useParams<{ hotelId: string; userId: string }>();
  const apiClient = useApiClient();
  const [search, setSearch] = useState('');

  const { data, isLoading, error } = useQuery<{ success: boolean; data: UserOffersResponse }>({
    queryKey: ['user-offers', hotelId, userId],
    queryFn: () => apiClient.get(`/hotels/${hotelId}/scanners/${userId}/offers`),
    enabled: !!hotelId && !!userId,
  });

  const offers = useMemo(() => data?.data?.offers ?? [], [data?.data?.offers]);

  const filteredOffers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return offers;
    return offers.filter((userOffer) => {
      const { name, title } = userOffer.promotionalOffer;
      return (
        (name?.toLowerCase().includes(q) ?? false) ||
        (title?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [offers, search]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-destructive">Failed to load promotional offers</div>
    );
  }

  return (
    <>
      <GuestListHeader
        title="Promotional Offers"
        description="Promotional offers assigned to this guest"
        search={
          offers.length
            ? { value: search, onChange: setSearch, placeholder: 'Search offers...' }
            : undefined
        }
      />

      {offers.length === 0 ? (
        <div className="rounded-xl border py-12 px-6">
          <EmptyTableState
            icon={<Gift className="h-8 w-8" />}
            title="No promotional offers"
            description="Promotional offers assigned to this guest for this hotel will appear here."
          />
        </div>
      ) : filteredOffers.length === 0 ? (
        <div className="rounded-xl border py-12 px-6">
          <EmptyTableState
            icon={<Search className="h-8 w-8" />}
            title="No matching offers"
            description="No offers match your search."
          />
        </div>
      ) : (
        <GuestOffersTable offers={filteredOffers} />
      )}
    </>
  );
}
